import { useTranslation } from "@refinedev/core";

import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";

import type { PatientConditions } from "@/utils/goal-utils";

export interface ConditionCheckboxesProps {
  conditions: PatientConditions;
  onConditionsChange: (conditions: PatientConditions) => void;
  showHeartFailureParent?: boolean;
}

export const ConditionCheckboxes = ({
  conditions,
  onConditionsChange,
  showHeartFailureParent = false,
}: ConditionCheckboxesProps) => {
  const { translate } = useTranslation();

  const update = (field: keyof PatientConditions, value: boolean) => {
    const next = { ...conditions, [field]: value };

    // When a child is checked, ensure the parent is also checked
    if (
      showHeartFailureParent &&
      value &&
      (field === "hasICD" || field === "hasLVAD" || field === "takesMedicationForWeightGain")
    ) {
      next.hasHeartFailure = true;
    }

    // When medication is unchecked, clear medication info
    if (field === "takesMedicationForWeightGain" && !value) {
      next.medicationInfo = "";
    }

    onConditionsChange(next);
  };

  const handleHeartFailureToggle = (checked: boolean) => {
    if (checked) {
      onConditionsChange({ ...conditions, hasHeartFailure: true });
    } else {
      // Unchecking parent clears all children
      onConditionsChange({
        ...conditions,
        hasHeartFailure: false,
        hasICD: false,
        hasLVAD: false,
        takesMedicationForWeightGain: false,
        medicationInfo: "",
      });
    }
  };

  const anyChildChecked = conditions.hasICD || conditions.hasLVAD || conditions.takesMedicationForWeightGain;
  const allChildrenChecked = conditions.hasICD && conditions.hasLVAD && conditions.takesMedicationForWeightGain;

  const checkboxItems = (
    <>
      <FormControlLabel
        control={
          <Checkbox checked={conditions.hasICD} onChange={e => update("hasICD", e.target.checked)} size="small" />
        }
        label={
          <Typography variant="body2">
            {translate(
              "pages.patientOverview.conditionParameters.hasICD",
              "Patient has Implantable Cardioverter Defibrillator (ICD)"
            )}
          </Typography>
        }
      />
      <FormControlLabel
        control={
          <Checkbox checked={conditions.hasLVAD} onChange={e => update("hasLVAD", e.target.checked)} size="small" />
        }
        label={
          <Typography variant="body2">
            {translate(
              "pages.patientOverview.conditionParameters.hasLVAD",
              "Patient has Left Ventricular Assist Device (LVAD)"
            )}
          </Typography>
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={conditions.takesMedicationForWeightGain}
            onChange={e => update("takesMedicationForWeightGain", e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2">
            {translate(
              "pages.patientOverview.conditionParameters.takesMedicationForWeightGain",
              "Patient takes medication for weight gain"
            )}
          </Typography>
        }
      />
    </>
  );

  if (!showHeartFailureParent) {
    return <Stack spacing={0}>{checkboxItems}</Stack>;
  }

  return (
    <Stack spacing={0}>
      <FormControlLabel
        control={
          <Checkbox
            checked={conditions.hasHeartFailure ?? anyChildChecked}
            indeterminate={anyChildChecked && !allChildrenChecked && (conditions.hasHeartFailure ?? true)}
            onChange={e => handleHeartFailureToggle(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color="text.primary">
            {translate("pages.patientOverview.conditionParameters.hasHeartFailure", "Heart Failure (HF)")}
          </Typography>
        }
      />
      <Stack spacing={0} pl={3}>
        {checkboxItems}
      </Stack>
    </Stack>
  );
};
